import { MoStatusLabelColor } from './components/MoStatusLabel';

import { invert } from './utils/objectUtils';
import throwWithoutBreaking from './utils/throwWithoutBreaking';

export type LookupType = { id: number; name: string };

export enum LocationEnum {
  REMOTE = 1,
  IN_PERSON = 2,
}

export const locationTypeToString: { [key in LocationEnum]: string } = {
  [LocationEnum.REMOTE]: 'Virtual',
  [LocationEnum.IN_PERSON]: 'In-person',
};

export const getLocationTypeStringFromId = (id: number) => {
  switch (id) {
    case LocationEnum.REMOTE:
      return locationTypeToString[LocationEnum.REMOTE];
    case LocationEnum.IN_PERSON:
      return locationTypeToString[LocationEnum.IN_PERSON];
    default:
      throwWithoutBreaking(new Error(`Invalid location type id: ${id}`));
      return '[Invalid location type]';
  }
};

export enum ZoomMeetingTypeEnum {
  SCHEDULED = 2,
  RECURRING_NO_FIXED_TIME = 3,
  RECURRING_WITH_FIXED_TIME = 8,
}

export enum RoleEnum {
  SUPERVISEE = 1,
  SUPERVISOR = 2,
  ORGANIZATION_ADMIN = 3,
  ANY,
}

export enum SessionLocationEnum {
  REMOTE = 1,
  IN_PERSON = 2,
}

export enum SessionChargeStatusEnum {
  INITIAL = 0,
  CHARGED = 1,
  DECLINED = 2,
  REFUNDED = 3,
  VOIDED = 4,
}

export enum SessionDispositionEnum {
  INITIAL = 0,
  COMPLETE = 4,
  CANCELLED = 5,
  CHARGED = 7,
}

export enum SessionTypeEnum {
  INDIVIDUAL = 2,
  DYAD = 3,
  GROUP = 4,
}

export const sessionTypeToString: { [key in SessionTypeEnum]: string } = {
  [SessionTypeEnum.INDIVIDUAL]: 'Individual session',
  [SessionTypeEnum.DYAD]: 'Dyad session',
  [SessionTypeEnum.GROUP]: 'Group session',
};

export const sessionTypeNameToId: { [key: string]: number } = {
  Individual: SessionTypeEnum.INDIVIDUAL,
  Dyad: SessionTypeEnum.DYAD,
  Group: SessionTypeEnum.GROUP,
};

export enum SessionRateEnum {
  DEFAULT = 12,
  DISCOUNTED = 7,
  LEGACY = 2,
  PRO_BONO = 3,
}

export const roleEnumToString: { [key in RoleEnum]: string } = {
  [RoleEnum.SUPERVISEE]: 'Supervisee',
  [RoleEnum.SUPERVISOR]: 'Supervisor',
  [RoleEnum.ORGANIZATION_ADMIN]: 'Organization Admin',
  [RoleEnum.ANY]: 'Any',
};

export const SUPERVISOR_BIO_WORD_MINIMUM = 50;

export enum SupervisorProfileDispositionEnum {
  NEEDS_APPROVAL = 0,
  PUBLISHED_AVAILABLE = 2,
  HIDDEN = 4,
}

export enum SupervisorBandwidthEnum {
  READY = 1,
  LIMITED = 2,
  NONE = 3,
}

export function getSupervisorBandwidthStringFromId(id: number | null | undefined) {
  if (id === SupervisorBandwidthEnum.READY) {
    return 'Ready';
  }
  if (id === SupervisorBandwidthEnum.LIMITED) {
    return 'Limited';
  }
  if (id === SupervisorBandwidthEnum.NONE) {
    return 'None';
  }
  return '';
}

export enum SupervisorDispositionFilter {
  ALL,
  PUBLISHED,
  UNPUBLISHED,
}
export const supervisorDispositionFilterToIds = {
  [SupervisorDispositionFilter.PUBLISHED]: [SupervisorProfileDispositionEnum.PUBLISHED_AVAILABLE],
  [SupervisorDispositionFilter.UNPUBLISHED]: [
    SupervisorProfileDispositionEnum.NEEDS_APPROVAL,
    SupervisorProfileDispositionEnum.HIDDEN,
  ],
  [SupervisorDispositionFilter.ALL]: [
    SupervisorProfileDispositionEnum.NEEDS_APPROVAL,
    SupervisorProfileDispositionEnum.HIDDEN,
    SupervisorProfileDispositionEnum.PUBLISHED_AVAILABLE,
  ],
};

export enum RecommendationStatusFilter {
  AWAITING_RESPONSE = 1,
  AWAITING_APPROVAL = 2,
  APPROVED = 3,
}

export enum SupervisorUnpublishReasonEnum {
  BANDWIDTH = 1,
  POOR_FIT = 2,
  NOT_OFFERING_SUPERVISION = 3,
  OTHER = 4,
  FINANCIAL = 5,
  UNRESPONSIVE = 6,
  NOT_ENOUGH_OPPORTUNITIES = 7,
  B2B_TRANSFER = 8,
  VETTING_INCOMPLETE = 9,
}

export const supervisorUnpublishReasonEnumToString: {
  [key in SupervisorUnpublishReasonEnum]: string;
} = {
  [SupervisorUnpublishReasonEnum.BANDWIDTH]: 'Bandwidth',
  [SupervisorUnpublishReasonEnum.POOR_FIT]: 'Motivo doesn’t fit their needs',
  [SupervisorUnpublishReasonEnum.NOT_OFFERING_SUPERVISION]: 'No longer offering supervision',
  [SupervisorUnpublishReasonEnum.OTHER]: 'Other',
  [SupervisorUnpublishReasonEnum.FINANCIAL]: 'Financial',
  [SupervisorUnpublishReasonEnum.UNRESPONSIVE]: 'Unresponsive',
  [SupervisorUnpublishReasonEnum.NOT_ENOUGH_OPPORTUNITIES]: 'Not enough opportunities',
  [SupervisorUnpublishReasonEnum.B2B_TRANSFER]: 'B2B transfer supervisor',
  [SupervisorUnpublishReasonEnum.VETTING_INCOMPLETE]: 'Vetting incomplete',
};

export function getSupervisorUnpublishReasonStringFromId(id: number) {
  if (id in supervisorUnpublishReasonEnumToString) {
    return supervisorUnpublishReasonEnumToString[id as SupervisorUnpublishReasonEnum];
  }

  return '';
}

export enum PurgeInitialSupervisorStatusEnum {
  PUBLISHED__INACTIVE = 1,
}

export enum UserDeleteReasonEnum {
  FINANCIAL = 1,
  NO_LONGER_OFFERING_SUPERVISION = 2,
  MOTIVO_DOESNT_FIT_THEIR_NEEDS = 3,
  COMPLIANCE = 4,
  CONDUCT = 5,
  DID_NOT_PROVIDE_REASON = 6,
  PERSONAL_REASON = 7,
  OTHER = 8,
}

export const stateIdToName: { [key: number]: string } = {
  1: 'Alabama',
  2: 'Alaska',
  3: 'Arizona',
  4: 'Arkansas',
  5: 'California',
  6: 'Colorado',
  7: 'Connecticut',
  8: 'Delaware',
  9: 'Florida',
  10: 'Georgia',
  11: 'Hawaii',
  12: 'Idaho',
  13: 'Illinois',
  14: 'Indiana',
  15: 'Iowa',
  16: 'Kansas',
  17: 'Kentucky',
  18: 'Louisiana',
  19: 'Maine',
  20: 'Maryland',
  21: 'Massachusetts',
  22: 'Michigan',
  23: 'Minnesota',
  24: 'Mississippi',
  25: 'Missouri',
  26: 'Montana',
  27: 'Nebraska',
  28: 'Nevada',
  29: 'New Hampshire',
  30: 'New Jersey',
  31: 'New Mexico',
  32: 'New York',
  33: 'North Carolina',
  34: 'North Dakota',
  35: 'Ohio',
  36: 'Oklahoma',
  37: 'Oregon',
  38: 'Pennsylvania',
  39: 'Rhode Island',
  40: 'South Carolina',
  41: 'South Dakota',
  42: 'Tennessee',
  43: 'Texas',
  44: 'Utah',
  45: 'Vermont',
  46: 'Virginia',
  47: 'Washington',
  48: 'West Virginia',
  49: 'Wisconsin',
  50: 'Wyoming',
  51: 'Washington DC',
  52: 'Canada',
  53: 'International',
  54: 'National',
};

export const usStatesPlusAll = Object.entries(stateIdToName).map(([id, name]) => ({
  id: Number(id),
  name,
}));

export const usStates = usStatesPlusAll.filter(({ id }) => id <= 51);

export const usStatesPlusNational = usStatesPlusAll.filter(({ id }) => id <= 51 || id === 54);

export const stateNameToId = invert(stateIdToName);

export const stateIdToAbbreviation: { [key: number]: string } = {
  1: 'AL',
  2: 'AK',
  3: 'AZ',
  4: 'AR',
  5: 'CA',
  6: 'CO',
  7: 'CT',
  8: 'DE',
  9: 'FL',
  10: 'GA',
  11: 'HI',
  12: 'ID',
  13: 'IL',
  14: 'IN',
  15: 'IA',
  16: 'KS',
  17: 'KY',
  18: 'LA',
  19: 'ME',
  20: 'MD',
  21: 'MA',
  22: 'MI',
  23: 'MN',
  24: 'MS',
  25: 'MO',
  26: 'MT',
  27: 'NE',
  28: 'NV',
  29: 'NH',
  30: 'NJ',
  31: 'NM',
  32: 'NY',
  33: 'NC',
  34: 'ND',
  35: 'OH',
  36: 'OK',
  37: 'OR',
  38: 'PA',
  39: 'RI',
  40: 'SC',
  41: 'SD',
  42: 'TN',
  43: 'TX',
  44: 'UT',
  45: 'VT',
  46: 'VA',
  47: 'WA',
  48: 'WV',
  49: 'WI',
  50: 'WY',
  51: 'DC',
};

export enum USStateEnum {
  Alabama = 1,
  Alaska = 2,
  Arizona = 3,
  Arkansas = 4,
  California = 5,
  Colorado = 6,
  Connecticut = 7,
  Delaware = 8,
  Florida = 9,
  Georgia = 10,
  Hawaii = 11,
  Idaho = 12,
  Illinois = 13,
  Indiana = 14,
  Iowa = 15,
  Kansas = 16,
  Kentucky = 17,
  Louisiana = 18,
  Maine = 19,
  Maryland = 20,
  Massachusetts = 21,
  Michigan = 22,
  Minnesota = 23,
  Mississippi = 24,
  Missouri = 25,
  Montana = 26,
  Nebraska = 27,
  Nevada = 28,
  'New Hampshire' = 29,
  'New Jersey' = 30,
  'New Mexico' = 31,
  'New York' = 32,
  'North Carolina' = 33,
  'North Dakota' = 34,
  Ohio = 35,
  Oklahoma = 36,
  Oregon = 37,
  Pennsylvania = 38,
  'Rhode Island' = 39,
  'South Carolina' = 40,
  'South Dakota' = 41,
  Tennessee = 42,
  Texas = 43,
  Utah = 44,
  Vermont = 45,
  Virginia = 46,
  Washington = 47,
  'West Virginia' = 48,
  Wisconsin = 49,
  Wyoming = 50,
  'Washington DC' = 51,
  Canada = 52,
  International = 53,
  National = 54,
}

export enum LicenseCategoryEnum {
  ADDICTION_COUNSELORS = 1,
  MARRIAGE_FAMILY_THERAPISTS = 2,
  PROFESSIONAL_MENTAL_HEALTH_COUNSELORS = 3,
  PSYCHOLOGISTS = 4,
  SOCIAL_WORKERS = 5,
  BEHAVIOR_ANALYSTS = 6,
}

export enum LicenseEnum {
  IMFT_S = 7,
  LICSW = 16,
  LCPC = 22,
  LPCC = 24,
  LPCC_S = 25,
  LPC_S = 27,
  QUALIFIED_SUPERVISOR = 58,
  LISAC = 64,
  LIAC = 97,
}

/**
 * @deprecated
 */
export const licenseCategoryNameToId: { [key: string]: number } = {
  'Addiction Counselor': 1,
  'Marriage and Family Therapist': 2,
  'Professional/Mental Health Counselor': 3,
  'Licensed Psychologist': 4,
  'Social Worker': 5,
  'Licensed Behavior Analyst': 6,
  'Board Certified Behavior Analyst': 7,
  'Bachelor’s Level Practitioner': 8,
  'Registered Behavior Technician': 9,
  'Peer Support Specialist': 10,
  Other: 100,
};

export const licenseCategoryIdToDisplayName: { [key: number]: string } =
  invert(licenseCategoryNameToId);

export enum IntroRequestStatusEnum {
  WAITING_ON_SUPERVISOR = 8,
  WAITING_ON_USER = 9,
  SCHEDULED = 10,
  ACCEPTED_BY_SUPERVISOR = 11,
  ACCEPTED_BY_USER = 12,
  ACCEPTED = 13,
  DECLINED_BY_SUPERVISOR = 14,
  DECLINED_BY_USER = 15,
  EXPIRED = 16,
  WAITING_ON_ADMIN_APPROVAL = 17,
  AVAILABILITY_REJECTED_BY_ADMIN = 18,
  AVAILABILITY_REJECTED_BY_EMPLOYER = 19,
}

export const CLOSED_INTRO_REQUEST_STATUSES = Object.freeze([
  IntroRequestStatusEnum.DECLINED_BY_USER,
  IntroRequestStatusEnum.DECLINED_BY_SUPERVISOR,
  IntroRequestStatusEnum.ACCEPTED,
  IntroRequestStatusEnum.EXPIRED,
]);

export enum JoinReasonEnum {
  LICENSURE = 1,
  PEER_CONSULTATION = 3,
  AGENCY_PAYING_FOR_STAFF = 6,
  SUPERVISOR = 7,
}

export const joinReasonEnumToString: { [key in JoinReasonEnum]: string } = {
  [JoinReasonEnum.LICENSURE]: 'Licensure',
  [JoinReasonEnum.PEER_CONSULTATION]: 'Peer Consultation',
  [JoinReasonEnum.AGENCY_PAYING_FOR_STAFF]: 'Agency Paying For Staff',
  [JoinReasonEnum.SUPERVISOR]: 'Supervisor',
};

export enum OrganizationSuperviseeOnboardingRequestStatusEnum {
  PENDING = 0,
  APPROVED = 1,
  DECLINED = 2,
  AWAITING_MATCH = 3,
}

export const clinicalSettingIdToName: { [key: number]: string } = {
  1: 'Private practice',
  2: 'Community-based agency',
  3: 'School-based counseling',
  4: 'Non-profit organization',
  5: 'Healthcare setting',
  1000: 'Other',
};

export enum IntroRequestDeclineReasonEnum {
  NO_BANDWIDTH = 1,
  NOT_OFFERING_SUPERVISION = 2,
  NOT_GOOD_MATCH = 3,
  NOT_SEEKING_SUPERVISION = 4,
  FOUND_ANOTHER_SUPERVISOR = 5,
  SUPERVISOR_UNRESPONSIVE = 6,
  UNPROFESSIONAL = 7,
  LIMITED_AVAILABILITY = 8,
  SUPERVISEE_NOT_READY = 9,
  NO_SHOW = 10,
}

export const legacyIntroRequestDeclineReasonIdToName: { [key: number]: string } = {
  1: 'Supervisee hasn’t graduated',
  2: 'Supervisee isn’t working in an approved clinical setting',
  3: 'Supervisee lives internationally',
  4: 'Selected supervisor is not able to accept supervisee',
  5: 'Supervisee requests a license type we don’t yet have',
  6: 'Supervisee has not yet started their coursework (BCBA only)',
};

export const introRequestDeclineReasonIdToName: { [key: number]: string } = {
  1: 'No bandwidth currently',
  2: 'No longer wish to offer clinical supervision on Motivo',
  3: 'Not a good match',
  4: 'No longer seeking supervision',
  5: 'Found another supervisor',
  6: 'Supervisor was unresponsive',
  7: 'Unprofessional',
  8: 'Limited availability',
  9: 'Supervisee is not ready to receive supervision',
  10: 'Didn’t show up',
};

export const introRequestStatusIdToName: { [key: number]: string } = {
  0: 'Pending',
  1: 'Active',
  2: 'Scheduled',
  3: 'Declined',
  4: 'Completed',
  5: 'Cancelled',
  6: 'No show',
  7: 'Pending',
  8: 'Waiting on supervisor',
  9: 'Waiting on participant',
  10: 'Scheduled',
  11: 'Accepted by supervisor',
  12: 'Accepted by participant',
  13: 'Accepted',
  14: 'Declined by supervisor',
  15: 'Declined by participant',
};

export enum SupervisorApplicationStatusEnum {
  PENDING = 0,
  APPROVED = 1,
  ONBOARDING = 2,
  COMPLETED = 3,
  DECLINED = 4,
}

export const supervisorApplicationStatusIdToName: { [key: number]: string } = {
  0: 'Pending',
  1: 'Approved',
  2: 'Onboarding',
  3: 'Completed',
  4: 'Declined',
};

export enum SupervisorApplicationOutreachStageEnum {
  EMAIL_1 = 1,
  EMAIL_2 = 2,
  EMAIL_3 = 3,
  INTERESTED = 4,
  CALL_SCHEDULED = 5,
  ENGAGED = 6,
}

export const supervisorApplicationOutreachStageIdToName: Record<
  SupervisorApplicationOutreachStageEnum,
  string
> = {
  [SupervisorApplicationOutreachStageEnum.ENGAGED]: 'Engaged',
  [SupervisorApplicationOutreachStageEnum.EMAIL_1]: 'Email 1',
  [SupervisorApplicationOutreachStageEnum.EMAIL_2]: 'Email 2',
  [SupervisorApplicationOutreachStageEnum.EMAIL_3]: 'Email 3',
  [SupervisorApplicationOutreachStageEnum.INTERESTED]: 'Interested',
  [SupervisorApplicationOutreachStageEnum.CALL_SCHEDULED]: 'Call Scheduled',
};

export enum FormQuestionTypesEnum {
  MULTIPLE_CHOICE = 1,
  FREE_TEXT = 2,
  TRUE_FALSE = 3,
  LIKERT_SCALE = 4,
  HOURS_AND_MINUTES = 5,
}

export const formQuestionTypeIdToName: { [key: number]: string } = {
  [FormQuestionTypesEnum.MULTIPLE_CHOICE]: 'Multiple choice',
  [FormQuestionTypesEnum.FREE_TEXT]: 'Free text',
  [FormQuestionTypesEnum.TRUE_FALSE]: 'True/False',
  [FormQuestionTypesEnum.LIKERT_SCALE]: 'Likert scale',
  [FormQuestionTypesEnum.HOURS_AND_MINUTES]: 'Hours and minutes',
};

export const CONTINUING_EDUCATION_PROFESSIONS = [
  'Social Worker',
  'Professional/Mental Health Counselor',
  'Marriage and Family Therapist',
  'Addiction Counselor',
  'Psychologist',
  'Other',
];

export enum CourseTypeEnum {
  ON_DEMAND = 0,
  LIVE = 1,
}

export enum CourseStatusEnum {
  DRAFT = 0,
  PUBLISHED = 1,
  HIDDEN = 2,
  UNLISTED = 3,
}

export enum BillableWorkRecordTypeEnum {
  OTHER = 1,
  CHART_AUDITING = 2,
  CLIENT_ASSESSMENT = 3,
  CLINICAL_PAPERWORK = 4,
  DIRECT_THERAPY = 5,
  SUPERVISON_PAPERWORK = 6,
  REVIEWING_CLINICAL_PAPERWORK = 7,
  PAYER_CREDENTIALING = 8,
  EMPLOYER_PAID_TRAINING = 9,
  EMPLOYER_PAID_CHECK_INS = 10,
  INTERN_STUDENT_DOCUMENTATION = 11,
  CLINICIAN_SETUP = 12,
}

export const BILLABLE_WORK_RECORD_TYPES_REQUIRING_SUPERVISEE = [
  BillableWorkRecordTypeEnum.REVIEWING_CLINICAL_PAPERWORK,
  BillableWorkRecordTypeEnum.INTERN_STUDENT_DOCUMENTATION,
] as const;

export const billableWorkRecordTypeRequiresSupervisee = (
  billableWorkRecordTypeId: number | undefined,
): boolean =>
  BILLABLE_WORK_RECORD_TYPES_REQUIRING_SUPERVISEE.some((id) => id === billableWorkRecordTypeId);

export enum AdminRoleEnum {
  SUPERADMIN = 1,
  BILLING = 2,
  COMMUNITY_OPERATIONS = 3,
  ACCOUNT_EXECUTIVE = 4,
  CONTINUING_EDUCATION = 5,
  COMMUNITY_COMPLIANCE = 6,
  SENIOR_LEADERSHIP = 7,
  SUPPORT = 8,
}

export enum OrganizationTierEnum {
  STRATEGIC = 1,
  EMERGING = 2,
  ESTABLISHED = 3,
  MAINTENANCE = 4,
}

export enum HelpAudienceEnum {
  SUPERVISEES = 'supervisees',
  SUPERVISORS = 'supervisors',
  EMPLOYERS = 'employers',
}

export enum PopulationEnum {
  CHILDREN = 1,
  TEENS = 2,
  YOUNG_ADULTS = 3,
  ADULTS = 4,
  OLDER_ADULTS = 5,
  COUPLES = 6,
  FAMILIES = 7,
}

/**
 * @deprecated query for data instead?
 */
export const populationOptions = [
  { id: PopulationEnum.CHILDREN, name: 'Children (0-12)' },
  { id: PopulationEnum.TEENS, name: 'Teens (13-18)' },
  { id: PopulationEnum.YOUNG_ADULTS, name: 'Young adults (19-29)' },
  { id: PopulationEnum.ADULTS, name: 'Adults (30-55)' },
  { id: PopulationEnum.OLDER_ADULTS, name: 'Older adults (55+)' },
  { id: PopulationEnum.COUPLES, name: 'Couples' },
  { id: PopulationEnum.FAMILIES, name: 'Families' },
];

export enum ChronosTaskStatusEnum {
  SCHEDULED = 0,
  RUNNING = 1,
  COMPLETED = 2,
  CANCELLED = 3,
  RETRYING = 4,
  TIMED_OUT = 5,
  SUPERSEDED = 6,
  FAILED = 100,
}

export enum ReferralSourceEnum {
  COLLEAGUE = 1,
  MOTIVO_SUPERVISEE = 2,
  MOTIVO_SUPERVISOR = 3,
  AGENCY = 4,
  SEARCH_ENGINE = 5,
  FACEBOOK = 6,
  LINKEDIN = 7,
  CHANNEL_PARTNER = 8,
  OTHER = 9,
}

export enum SupervisorReferralSourceEnum {
  GOOGLE_SEARCH_ENGINE = 1,
  LINKEDIN = 2,
  FACEBOOK = 3,
  INSTAGRAM = 4,
  WORD_OF_MOUTH = 5,
  PARTNER_CAPELLA_LIBERTY_AAMFT_ETC = 6,
  CONFERENCE = 7,
  OTHER = 8,
  INDEED = 9,
  COLLEAGUE_FRIEND = 10,
}

export const referralSourceSlugToText = new Map([
  ['alma', 'Alma Partnership'],
  ['Alma Partnership', 'Alma Partnership'],
]);

export enum organizationSizeIdEnum {
  UP_TO_9 = 1,
  UP_TO_49 = 2,
  UP_TO_199 = 3,
  UP_TO_499 = 4,
  UP_TO_999 = 5,
  UP_TO_4999 = 6,
}

export enum OrganizationTypeIdEnum {
  ARMHS_PROGRAM = 1,
  COMMUNITY_MENTAL_HEALTH_PROVIDER = 2,
  DIGITAL_VIRTUAL_MENTAL_HEALTH_COMPANY = 3,
  GROUP_PRACTICE = 4,
  TREATMENT_CENTER = 5,
  HEALTH_SYSTEM = 6,
  PAYER = 7,
  OTHER = 100,
}

export enum OrganizationReferralSourceIdEnum {
  MARKETING_CAMPAIGN = 1,
  CLIENT_EVENT = 2,
  CONFERENCE = 3,
  CLIENT_REFERRAL = 4,
  ADVISOR_INTRODUCTION = 5,
  WEBINAR = 6,
  WEBSITE_INBOUND_INQUIRY = 7,
  EMPLOYEE_REFERRAL = 8,
  EXTERNAL_REFERRAL = 9,
  CALENDLY = 10,
  OTHER = 11,
  CHATGPT = 12,
  CUSTOMER_REFERRAL = 13,
  GOOGLE = 14,
  LINKEDIN = 15,
  THERAPIST_REFERRAL = 16,
}

export enum MatchStatusEnum {
  INACTIVE = 0,
  AWAITING_CREDIT_CARD = 1,
  AWAITING_B2B_SUPERVISOR_APPROVAL = 2,
  AWAITING_BOARD_APPROVAL = 3,
  INACTIVE_FROM_PAYMENT_DECLINE = 4,
  DECLINED = 5,
  ACTIVE = 100,
}

export enum UnmatchSuperviseeReason {
  PERSONAL = 1,
  FINANCIAL = 2,
  NOT_GOOD_FIT = 3,
  ACHIEVED_LICENSURE = 4,
  OTHER = 5,
  SCHEDULES_DO_NOT_ALIGN = 6,
  ETHICAL_PROFESSIONAL_CONCERNS = 7,
  TRANSITIONED_TO_INTERNAL_SUPERVISION = 8,
  LEFT_THE_ORGANIZATION = 9,
  SUPERVISEE_UNRESPONSIVE = 10,
  TEMPORARY_COVERAGE_MATCH_ENDED = 11,
  LEAVING_MOTIVO = 12,
  INCIDENT_CONDUCT_ISSUE = 13,
  COMPLETED_PRACTICUM = 14,
  ROSTER_INACTIVITY = 15,
}

export enum SupervisorApplicationDeclineReasonEnum {
  RATES_TOO_LOW = 1,
  UNRESPONSIVE_TO_OUTREACH = 2,
  NO_BANDWIDTH = 3,
  OTHER = 4,
}

export enum OpportunityTypeEnum {
  SUPERVISEE = 1,
  ORGANIZATION = 2,
}

export function getOpportunityTypeLabel(opportunityTypeId: number) {
  return opportunityTypeId === OpportunityTypeEnum.ORGANIZATION ? 'Employer intro' : 'Supervisee';
}

export enum OpportunityStatusEnum {
  OPEN = 0,
  MATCHED = 1,
  CLOSED = 2,
}

export enum OpportunitiesCandidateStatusEnum {
  PENDING = 0,
  REQUESTED = 1,
  DECLINED = 2,
  APPROVED = 3,
  INTERESTED = 4,
  EVALUATING = 5,
  CLOSED = 6,
}

export const EVALUATING_CANDIDATES_LIMIT = 2;

export enum OpportunitiesApplicationStatusEnum {
  SUBMITTED = 0,
  APPROVED = 1,
  DECLINED = 2,
  WITHDRAWN = 3,
}

export enum OpportunitiesBlockReasonEnum {
  AT_FOR_PROFIT_MAX_CA = 1,
  DECLINED_AFTER_EMPLOYER_INTRO = 2,
  PRIOR_CLINICAL_INCIDENT = 3,
  UNMATCHED_WITH_SUPERVISEE = 4,
  APPLICATION_REJECTED = 5,
  DIDNT_MEET_REQUIREMENTS = 6,
  MANUALLY_BLOCKED = 7,
  EXCLUDED_FROM_ORGANIZATION = 8,
  AUTO_REJECTED_AFTER_MATCH = 9,
  AUTO_REJECTED_AFTER_CLOSE = 10,
  MANUALLY_REMOVED = 11,
  DECLINED_OPPORTUNITY = 12,
}

export const OpportunitiesCandidatesEventsDescriptions: { [key: string]: string } = {
  opportunitiesCandidateViewed: 'viewed this opportunity',
  opportunitiesCandidateInterested: 'marked interest',
  opportunitiesCandidateEvaluating: 'moved into the evaluation stage',
  opportunitiesCandidateRequested: 'sent a request',
  opportunitiesCandidateDeclined: 'declined this opportunity',
  opportunitiesCandidateIntroScheduled: 'scheduled an intro call',
  opportunitiesCandidateIntroCancelled: 'cancelled an intro call',
  opportunitiesCandidateSupportRequested: 'requested support',
  opportunitiesCandidatePartiallyMatched: 'has partially matched',
  opportunitiesCandidateMatched: 'has marked this opportunity as matched',
  opportunitiesCandidateIntroCallInviteSent: 'sent an intro call invite',
  opportunitiesCandidateChatInitiated: 'initiated a chat',
  opportunitiesCandidateIntroCallScheduled: 'scheduled an intro call',
  opportunitiesCandidateIntroCallCancelled: 'cancelled an intro call',
  opportunitiesCandidateManuallyMatchedByAccountManager: 'manually matched',
  opportunitiesCandidateAvailabilitySubmitted: 'submitted availability',
  opportunitiesCandidateAvailabilityRejected: 'requested new availability',
  opportunitiesCandidateAvailabilityApproved: 'sent availability to employer',
  opportunitiesCandidateOrganizationAdminBooked: 'booked intro call',
  opportunitiesCandidateOrganizationAdminRejectedProposedTimes: 'requested new meeting times',
  opportunitiesCandidateApplicationApproved: 'approved their job board application',
};

export function opportunitiesCandidateEventDescription(
  eventDescription: string,
  supervisorName?: string | null,
) {
  if (eventDescription === 'opportunitiesCandidateApplicationApproved' && supervisorName) {
    return `approved ${supervisorName}’s job board application`;
  }

  return OpportunitiesCandidatesEventsDescriptions[eventDescription];
}

export enum OpportunitiesCandidateDispositionEnum {
  PUBLISHED = 0,
  UNPUBLISHED = 1,
  WAITLIST = 2,
}

export enum OrganizationRoleEnum {
  ADMIN = 1,
  CLINICAL_DIRECTOR = 2,
  PROGRAM_DIRECTOR = 3,
  BILLING_MANAGER = 4,
}

export const organizationAdminRoleIdToName: { [key: number]: string } = {
  1: 'Admin',
  2: 'Clinical director',
  3: 'Program director',
  4: 'Billing manager',
};

export const roleDescriptions: { [key: number]: string } = {
  1: 'Oversees sessions, supervisees, and billing. Can create programs and add other team members.',
  2: 'Manages their programs. Can add program directors of programs they manage. Cannot see billing.',
  3: 'Manages the supervisees of their assigned program. Cannot see billing.',
  4: 'As billing manager, you have access to sessions and billing.',
};

export const tierDescriptions: { [key: number]: string } = {
  1: 'High revenue, high growth potential',
  2: 'Low revenue, high growth potential',
  3: 'High revenue, low growth potential',
  4: 'Low revenue, low growth potential',
};

export const BASE_DURATION_OPTIONS = [30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180];

export enum UserNotificationTriggerEnum {
  INTRO_CALL_REQUESTED = 1,
  INTRO_CALL_REMINDER_INACTION = 2,
  INTRO_CALL_REMINDER_1_HOUR = 3,
  INTRO_CALL_REMINDER_24_HOURS = 4,
  SESSION_REQUESTED = 5,
  SESSION_REQUEST_REMINDER_INACTION = 6,
  SESSION_BOOKED = 7,
  SESSION_REMINDER_1_HOUR = 8,
  SESSION_REMINDER_24_HOURS = 9,
  SESSION_REMINDER_48_HOURS = 10,
  UNREAD_CHAT_MESSAGE = 11,
  UNREAD_CHAT_MESSAGE_REMINDER = 12,
  OPPORTUNITY_RECEIVED = 13,
  JOB_BOARD_OPPORTUNITY_POSTED = 14,
}

export enum FrequencyEnum {
  ALL_TIME = 1,
  MONTHLY = 2,
  WEEKLY = 3,
  QUARTERLY = 4,
}

export enum BillableWorkRecordCapTypeEnum {
  PER_SUPERVISOR = 1,
  PER_SUPERVISEE = 2,
}

export enum IntroCallPreferences {
  NONE = 1,
  EMPLOYER_ADMIN_WITH_SUPERVISOR = 2,
  SUPERVISEE_WITH_SUPERVISOR = 3,
}

export const introCallPreferenceIdToName = new Map([
  [IntroCallPreferences.NONE, 'No intro call needed'],
  [IntroCallPreferences.EMPLOYER_ADMIN_WITH_SUPERVISOR, 'Employer admin does intro call'],
  [IntroCallPreferences.SUPERVISEE_WITH_SUPERVISOR, 'Supervisee does intro call'],
]);

export enum ExternalSupervisorRequestStatusEnum {
  PAUSED = 4,
  NOT_STARTED = 10,
  WAITLIST_OUTREACH = 11,
  EXTERNAL_OUTREACH = 12,
  INTRODUCTORY_INTERVIEWS = 13,
  AM_MATCHING = 14,
  COMPLETED = 15,
  WITHDRAWN_INTERNAL = 16,
  WITHDRAWN_EXTERNAL = 17,
}

export enum ExternalSupervisorRequestSourceEnum {
  EXTERNAL = 1,
  INTERNAL = 2,
  WAITLIST = 3,
}

export enum ExternalSupervisorRequestCandidateStageEnum {
  CONTACTED = 1,
  MEETING_SCHEDULED = 2,
  APPLICATION_APPROVED = 3,
  ONBOARDING = 4,
  VETTING = 5,
  PUBLISHED = 6,
  REJECTED = 7,
  UNRESPONSIVE = 8,
}

export const ExternalSupervisorRequestCandidateStageLabels: Record<
  ExternalSupervisorRequestCandidateStageEnum,
  string
> = {
  [ExternalSupervisorRequestCandidateStageEnum.CONTACTED]: 'Contacted',
  [ExternalSupervisorRequestCandidateStageEnum.MEETING_SCHEDULED]: 'Meeting scheduled',
  [ExternalSupervisorRequestCandidateStageEnum.APPLICATION_APPROVED]: 'Application approved',
  [ExternalSupervisorRequestCandidateStageEnum.ONBOARDING]: 'Onboarding',
  [ExternalSupervisorRequestCandidateStageEnum.VETTING]: 'Vetting',
  [ExternalSupervisorRequestCandidateStageEnum.PUBLISHED]: 'Published',
  [ExternalSupervisorRequestCandidateStageEnum.REJECTED]: 'Rejected',
  [ExternalSupervisorRequestCandidateStageEnum.UNRESPONSIVE]: 'Unresponsive',
};

export enum ExternalSupervisorRequestUrgencyEnum {
  SUPERVISEE_WAITING = 1,
  EMPLOYER_REQUEST = 2,
  PROACTIVE_SUPPLY = 4,
}

export enum SupervisorVettingStatusEnum {
  NEW = 1,
  IN_PROGRESS = 2,
  AWAITING_RESPONSE = 3,
  ESCALATED = 4,
  VETTING_PAUSED = 5,
  VETTED = 6,
  UNABLE_TO_VET = 7,
  NEEDS_RE_VETTING = 8,
}

export const DEFAULT_SUPERVISOR_PAYOUT_RATES_IN_CENTS = {
  individual: 5500,
  dyad: 7500,
  group: 9000,
};

export enum GrantTypeEnum {
  ANTHEM = 1,
  OPTUM = 2,
}

export const grantIdToName: { [key: number]: string } = {
  1: 'Anthem',
  2: 'Optum',
};

export const grantTypes = Object.entries(grantIdToName).map(([id, name]) => ({
  id: Number(id),
  name,
}));

export enum ActorTypeEnum {
  USER = 'user',
  ADMIN = 'admin',
  EMPLOYER_ADMIN = 'organization_admin',
  SYSTEM = 'system',
}

export enum IncidentReportStatusEnum {
  OPEN = 0,
  RESOLVED = 1,
  INVESTIGATING = 2,
  REMEDIATING = 3,
  AWAITING_COMMITTEE_REVIEW = 4,
  PENDING_TERMINATION = 5,
}

export enum OrganizationIncidentStatusEnum {
  OPEN = 0,
  INVESTIGATING = 1,
  REMEDIATING = 2,
  RESOLVED = 3,
}

export enum IncidentReporterEnum {
  SUPERVISEE_ASSOCIATE = 1,
  EMPLOYER_CARE_MANAGER = 2,
  EMPLOYER = 3,
  SUPERVISOR = 5,
  VETTING = 7,
  AUDIT = 8,
}

export enum SupervisorIncidentCommitteeReviewStatusEnum {
  PENDING = 0,
  REFERRED_BACK = 1,
  APPROVED = 2,
}

export enum SupervisorIncidentResolutionRecommendationTypeEnum {
  NO_ACTION = 0,
  OFFER_TRAINING = 1,
  WARNING = 3,
  STRIKE = 4,
  TERMINATION = 5,
  OTHER = 6,
}

export enum SessionCancellationReasonEnum {
  WORK_OR_PROFESSIONAL_CONFLICT = 1,
  PERSONAL_ISSUE = 2,
  SCHEDULING_MISCOMMUNICATION = 3,
  TECHNICAL_ISSUE = 4,
  SESSION_NOT_NEEDED = 5,
  OTHER = 6,
}

export enum TemporaryMatchReasonEnum {
  PLANNED_ABSENCE = 1,
  INCIDENT_OR_CONDUCT_INVESTIGATION = 2,
  RECRUITING_A_PERMANENT_REPLACEMENT = 3,
  STATE_BOARD_OR_REGULATORY_CHANGE = 4,
  OTHER = 1000,
}

export enum CommercialStatusEnum {
  FOR_PROFIT = 1,
  NON_PROFIT = 2,
}

export enum ProfitStatusesEnum {
  FOR_PROFIT = 1,
  NON_PROFIT = 2,
}

export enum StudentsInternsScopeEnum {
  ALL = 1,
  SOME = 2,
  NONE = 3,
}

export const studentsInternsScopeIdToName: { [key in StudentsInternsScopeEnum]: string } = {
  [StudentsInternsScopeEnum.ALL]: 'Yes, all of them',
  [StudentsInternsScopeEnum.SOME]: 'Yes, some of them',
  [StudentsInternsScopeEnum.NONE]: 'No, none',
};

export function getIsStudentStatusLabel(isStudent: boolean | null | undefined) {
  if (isStudent === null || isStudent === undefined) return '-';
  return isStudent ? 'Yes' : 'No';
}

export const incidentReportStatusColors = new Map<IncidentReportStatusEnum, MoStatusLabelColor>([
  [IncidentReportStatusEnum.OPEN, 'error'],
  [IncidentReportStatusEnum.INVESTIGATING, 'secondary'],
  [IncidentReportStatusEnum.AWAITING_COMMITTEE_REVIEW, 'warning'],
  [IncidentReportStatusEnum.REMEDIATING, 'secondary'],
  [IncidentReportStatusEnum.RESOLVED, 'success'],
  [IncidentReportStatusEnum.PENDING_TERMINATION, 'warning'],
]);

export enum OpportunityPreferenceEnum {
  EITHER = 1,
  B2C = 2,
  B2B = 3,
}

export const idToDayOfWeek = new Map([
  [1, 'Monday'],
  [2, 'Tuesday'],
  [3, 'Wednesday'],
  [4, 'Thursday'],
  [5, 'Friday'],
  [6, 'Saturday'],
  [7, 'Sunday'],
]);

export enum SuperviseeTypeEnum {
  STUDENT_OR_INTERN = 1,
  ASSOCIATE = 2,
  SUPERVISOR_IN_TRAINING = 3,
  BACHELORS_LEVEL_PRACTITIONER = 4,
  PEER_SUPPORT = 5,
}

export type StudentSuperviseeProfile = {
  isStudent?: boolean | null;
  superviseeTypeId?: number | null;
};

export function isStudentSupervisee(
  superviseeProfile: StudentSuperviseeProfile | null | undefined,
) {
  return (
    superviseeProfile?.superviseeTypeId === SuperviseeTypeEnum.STUDENT_OR_INTERN ||
    superviseeProfile?.isStudent === true
  );
}

export enum EmploymentStatusEnum {
  FULL_TIME = 1,
  PART_TIME = 2,
  CONTRACT = 3,
}

export enum WorkModalityEnum {
  VIRTUAL = 1,
  IN_PERSON = 2,
  HYBRID = 3,
}

export const superviseeTypeNameToId: { [key: string]: number } = {
  'Student or intern': SuperviseeTypeEnum.STUDENT_OR_INTERN,
  Associate: SuperviseeTypeEnum.ASSOCIATE,
  'Supervisor-in-training': SuperviseeTypeEnum.SUPERVISOR_IN_TRAINING,
  // eslint-disable-next-line motivo/prefer-smart-quotes
  "Bachelor's-level practitioner": SuperviseeTypeEnum.BACHELORS_LEVEL_PRACTITIONER,
  'Peer support or other non-clinical role': SuperviseeTypeEnum.PEER_SUPPORT,
};

export const employmentStatusNameToId: { [key: string]: number } = {
  'Full-time': EmploymentStatusEnum.FULL_TIME,
  'Part-time': EmploymentStatusEnum.PART_TIME,
  Contract: EmploymentStatusEnum.CONTRACT,
};

export const workModalityNameToId: { [key: string]: number } = {
  Virtual: WorkModalityEnum.VIRTUAL,
  'In-person': WorkModalityEnum.IN_PERSON,
  Hybrid: WorkModalityEnum.HYBRID,
};

export enum PayerTypeEnum {
  MEDICAID = 1,
}

export enum SupervisionModelEnum {
  EXTERNAL = 1,
  EXTERNAL_AND_INTERNAL = 2,
  INTERNAL = 3,
}

export const HELP_CENTER_URL = 'https://motivohealth.com/help';

export const HELP_ARTICLE_URLS = {
  organizationHelpCenter: `${HELP_CENTER_URL}/employers`,
  supervisorWaitlist: `${HELP_CENTER_URL}/supervisors/getting-started/what-happens-after-you-apply#the-supervisor-waitlist`,
  employerSponsoredSupervision: `${HELP_CENTER_URL}/supervisors/working-with-employers/self-pay-and-employer-sponsored-supervision`,
  connectCalendar: `${HELP_CENTER_URL}/supervisors/availability-and-calendars/connect-google-or-microsoft-calendar`,
  sessionRecords: `${HELP_CENTER_URL}/supervisors/sessions/write-sign-and-share-session-records`,
  startOnlineSession: `${HELP_CENTER_URL}/supervisors/sessions/start-an-online-session#if-nothing-opens`,
  addLicenses: `${HELP_CENTER_URL}/supervisors/licenses-and-credentials/add-or-update-licenses-and-credentials`,
  superviseeBoardDocuments: `${HELP_CENTER_URL}/supervisees/licensure-progress/download-hours-and-gather-board-required-documents`,
} as const;
